/*
 * elevation-terrain-sink.ts - build elevation-store units from terrain
 */

import type ElevationStore from './elevation-store';
import type MapSurfaceTile from './surface-tile';
import type GpuTexture from '../renderer/gpu/texture';
import type * as viewerConfig from '../viewer-config';
import { TileRenderRig } from './tile-render-rig';


/**
 * Builds one elevation-store unit per node, between `beginNode` and
 * `endNode`, from the terrain drawn in an elevation pass.
 *
 * The pass rasterizes heights, so readiness rests on the mesh alone;
 * whether a tile may fetch its mesh is the pass's descent's decision.
 * An unready rig contributes nothing. With
 * `mapElevationStoreFallbackDraws` off, a fallback draw that fetches
 * nothing is refused as well.
 */
export class ElevationTerrainSink {

    constructor(
        store: ElevationStore,
        config: Readonly<viewerConfig.ViewerConfig>,
    ) {

        this.store = store;
        this.config = config;
    }

    beginNode(tileId: [number, number, number]): void {

        this.store.beginUnit(tileId);
    }

    isReady(
        rig: TileRenderRig,
        readiness: TileRenderRig.ReadinessLevels,
        priority: TileRenderRig.Priority,
        options: TileRenderRig.IsReadyOptions,
    ): boolean {

        if (options.doNotLoad && readiness.desired === 'fallback'
                && !this.config.mapElevationStoreFallbackDraws) return false;

        return rig.isDepthReady(priority.essential, options);
    }

    draw(
        tile: MapSurfaceTile,
        rig: TileRenderRig,
        maskTexture?: GpuTexture,
    ): void {

        this.store.drawUnit(tile, rig, maskTexture);
    }

    endNode(
        tileId: [number, number, number],
        covered: boolean,
        watertight: boolean,
    ): void {

        this.store.endUnit(tileId, covered, watertight);
    }

    private readonly store: ElevationStore;
    private readonly config: Readonly<viewerConfig.ViewerConfig>;
}

export default ElevationTerrainSink;
